/* eslint-disable react-refresh/only-export-components */
import { useState, useCallback } from 'react'
import { FiX, FiChevronUp, FiChevronDown } from 'react-icons/fi'
import type { ThemeSummary } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'

// ── Types ─────────────────────────────────────────────────────────────────────

export type ActivityType =
  | 'wordsearch'
  | 'sudoku'
  | 'maths'
  | 'attention'
  | 'symmetry'
  | 'whats_missing'
  | 'roman'
  | 'hours'
  | 'synonyms'

export type NotebookBuilderItem =
  | { itemType: 'random'; spec: { type: 'wordsearch'; themeIds: number[]; difficulty: string; mode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'sudoku'; difficulty: string; mode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'maths'; operation: string; difficulty: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'attention'; difficulty: string; count: number; themeIds?: number[]; mode?: string }; label: string }
  | { itemType: 'random'; spec: { type: 'symmetry'; difficulty: string; mode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'whats_missing'; difficulty: string; mode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'roman'; difficulty: string; printMode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'hours'; difficulty: string; clockType: string; writeMode: string; count: number }; label: string }
  | { itemType: 'random'; spec: { type: 'synonyms'; difficulty: string; gameMode: string; printMode: string; count: number }; label: string }

// ── Helpers ───────────────────────────────────────────────────────────────────

function wsWordCount(theme: ThemeSummary, language: string, difficulty: string): number {
  const lang = language === 'en' ? 'en' : 'es'
  const diff = difficulty as 'easy' | 'medium' | 'hard'
  return theme.wordsearchFit?.[lang]?.[diff] ?? 0
}

function wsIncompatible(theme: ThemeSummary, language: string, difficulty: string): boolean {
  return wsWordCount(theme, language, difficulty) < 4
}

function clampCount(val: string, min = 1, max = 50): number {
  return Math.max(min, Math.min(max, parseInt(val) || min))
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseNotebookItemsBuilderOptions {
  language: string
  wordsearchThemes: ThemeSummary[]
  attentionThemes: ThemeSummary[]
  /** Translation helper for difficulty labels */
  diffLabels: { easy: string; medium: string; hard: string }
  /** Translation object for game labels */
  games: Record<string, { label: string }>
  /** Pre-populate with existing items (for edit mode) */
  initialItems?: NotebookBuilderItem[]
}

export function useNotebookItemsBuilder({
  language,
  wordsearchThemes,
  attentionThemes,
  diffLabels,
  games,
  initialItems,
}: UseNotebookItemsBuilderOptions) {
  const [items, setItems] = useState<NotebookBuilderItem[]>(initialItems ?? [])
  const [addMode, setAddMode] = useState<ActivityType | null>(null)

  // Wordsearch form
  const [wsDiff, setWsDiff] = useState('medium')
  const [wsCount, setWsCount] = useState('5')
  const [wsThemeIds, setWsThemeIds] = useState<number[]>([])
  const [wsMode, setWsMode] = useState('simple')

  // Sudoku form
  const [sdDiff, setSdDiff] = useState('medium')
  const [sdMode, setSdMode] = useState('standard')
  const [sdCount, setSdCount] = useState('5')

  // Maths form
  const [mtOp, setMtOp] = useState('addition')
  const [mtDiff, setMtDiff] = useState('medium')
  const [mtCount, setMtCount] = useState('1')

  // Attention form
  const [atDiff, setAtDiff] = useState('medium')
  const [atCount, setAtCount] = useState('1')
  const [atThemeIds, setAtThemeIds] = useState<number[]>([])
  const [atMode, setAtMode] = useState<'words' | 'numbers'>('words')

  // Symmetry form
  const [symDiff, setSymDiff] = useState('medium')
  const [symMode, setSymMode] = useState('vertical')
  const [symCount, setSymCount] = useState('1')

  // What's missing form
  const [wmDiff, setWmDiff] = useState('medium')
  const [wmMode, setWmMode] = useState<'emoji' | 'shapes'>('emoji')
  const [wmCount, setWmCount] = useState('1')

  // Roman form
  const [romDiff, setRomDiff] = useState('medium')
  const [romPrintMode, setRomPrintMode] = useState('join')
  const [romCount, setRomCount] = useState('1')

  // Hours form
  const [hrsDiff, setHrsDiff] = useState<'join' | 'write'>('join')
  const [hrsClockType, setHrsClockType] = useState('analog')
  const [hrsWriteMode, setHrsWriteMode] = useState('write_words')
  const [hrsCount, setHrsCount] = useState('1')

  // Synonyms form
  const [synGameMode, setSynGameMode] = useState('synonyms')
  const [synPrintMode, setSynPrintMode] = useState('write')
  const [synCount, setSynCount] = useState('1')

  const gameLabel = useCallback(
    (slug: string) => games[slug]?.label ?? slug,
    [games],
  )

  function toggleTheme(id: number, setter: React.Dispatch<React.SetStateAction<number[]>>) {
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function addWordsearch() {
    const count = clampCount(wsCount, 1, 50)
    const diffLabel = diffLabels[wsDiff as keyof typeof diffLabels] ?? wsDiff
    const selectedThemes = wsThemeIds.length > 0 ? wsThemeIds : wordsearchThemes.map(t => t.id)
    const themeNames = wsThemeIds.length > 0
      ? wsThemeIds.map(id => wordsearchThemes.find(t => t.id === id)?.[language === 'en' ? 'nameEn' : 'nameEs'] ?? '').filter(Boolean)
      : []
    const label = `×${count} ${gameLabel('wordsearch')} · ${diffLabel}${wsMode !== 'simple' ? ` · ${wsMode}` : ''}${themeNames.length ? ` · ${themeNames.join(', ')}` : ''}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'wordsearch', themeIds: selectedThemes, difficulty: wsDiff, mode: wsMode, count },
      label,
    }])
    setAddMode(null)
    setWsThemeIds([])
  }

  function addSudoku() {
    const count = clampCount(sdCount, 1, 50)
    const diffLabel = diffLabels[sdDiff as keyof typeof diffLabels] ?? sdDiff
    const modeLabel = sdMode === 'mini' ? '4×4' : '9×9'
    const label = `×${count} ${gameLabel('sudoku')} ${modeLabel} · ${diffLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'sudoku', difficulty: sdDiff, mode: sdMode, count },
      label,
    }])
    setAddMode(null)
  }

  function addMaths() {
    const count = clampCount(mtCount, 1, 50)
    const diffLabel = diffLabels[mtDiff as keyof typeof diffLabels] ?? mtDiff
    const opNames: Record<string, string> = { addition: 'Suma', subtraction: 'Resta', multiplication: 'Multiplicación', division: 'División' }
    const opIcons: Record<string, string> = { addition: '+', subtraction: '−', multiplication: '×', division: '÷' }
    const label = `${count} página${count !== 1 ? 's' : ''} · ${opNames[mtOp] ?? mtOp} ${opIcons[mtOp] ?? ''} · ${diffLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'maths', operation: mtOp, difficulty: mtDiff, count },
      label,
    }])
    setAddMode(null)
  }

  function addAttention() {
    const count = clampCount(atCount, 1, 50)
    const diffLabel = diffLabels[atDiff as keyof typeof diffLabels] ?? atDiff
    const resolvedThemeIds = atMode === 'words'
      ? (atThemeIds.length > 0 ? atThemeIds : attentionThemes.map(t => t.id))
      : undefined
    const themeNames = (resolvedThemeIds ?? []).map(id => attentionThemes.find(t => t.id === id)?.[language === 'en' ? 'nameEn' : 'nameEs'] ?? '').filter(Boolean)
    const modeLabel = atMode === 'numbers' ? ` · ${language === 'en' ? 'Numbers' : 'Números'}` : ''
    const label = `×${count} ${gameLabel('attention')} · ${diffLabel}${modeLabel}${themeNames.length ? ` · ${themeNames.join(', ')}` : ''}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'attention', difficulty: atDiff, count, themeIds: resolvedThemeIds, mode: atMode },
      label,
    }])
    setAddMode(null)
    setAtThemeIds([])
  }

  function addSymmetry() {
    const count = clampCount(symCount, 1, 50)
    const diffLabel = diffLabels[symDiff as keyof typeof diffLabels] ?? symDiff
    const modeLabels: Record<string, string> = { vertical: '↔', horizontal: '↕', quad: '✦' }
    const label = `${count} página${count !== 1 ? 's' : ''} · ${gameLabel('symmetry')} ${modeLabels[symMode] ?? ''} · ${diffLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'symmetry', difficulty: symDiff, mode: symMode, count },
      label,
    }])
    setAddMode(null)
  }

  function addWhatsMissing() {
    const count = clampCount(wmCount, 1, 50)
    const diffLabel = diffLabels[wmDiff as keyof typeof diffLabels] ?? wmDiff
    const modeLabel = wmMode === 'emoji' ? 'Emojis' : (language === 'en' ? 'Shapes' : 'Formas')
    const label = `×${count} ${gameLabel('whats_missing')} · ${modeLabel} · ${diffLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'whats_missing', difficulty: wmDiff, mode: wmMode, count },
      label,
    }])
    setAddMode(null)
  }

  function addRoman() {
    const count = clampCount(romCount, 1, 50)
    const diffLabel = diffLabels[romDiff as keyof typeof diffLabels] ?? romDiff
    const printModeLabel = romPrintMode === 'join'
      ? (language === 'en' ? 'Match' : 'Unir')
      : (language === 'en' ? 'Write' : 'Escribir')
    const label = `${count} página${count !== 1 ? 's' : ''} · ${gameLabel('roman')} · ${diffLabel} · ${printModeLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'roman', difficulty: romDiff, printMode: romPrintMode, count },
      label,
    }])
    setAddMode(null)
  }

  function addHours() {
    const count = clampCount(hrsCount, 1, 50)
    const diffLabel = hrsDiff === 'join'
      ? (language === 'en' ? 'Match' : 'Une')
      : (language === 'en' ? 'Write' : 'Escribe')
    const clockLabel = hrsClockType === 'mixed'
      ? (language === 'en' ? 'Mixed' : 'Mixtos')
      : hrsClockType === 'analog'
        ? (language === 'en' ? 'Analog' : 'Analógico')
        : (language === 'en' ? 'Digital' : 'Digital')
    const label = `${count} página${count !== 1 ? 's' : ''} · ${gameLabel('hours')} · ${diffLabel} · ${clockLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'hours', difficulty: hrsDiff, clockType: hrsClockType, writeMode: hrsWriteMode, count },
      label,
    }])
    setAddMode(null)
  }

  function addSynonyms() {
    const count = clampCount(synCount, 1, 50)
    const gameModeLabels: Record<string, string> = {
      synonyms: language === 'en' ? 'Synonyms' : 'Sinónimos',
      antonyms: language === 'en' ? 'Antonyms' : 'Antónimos',
      mixed:    language === 'en' ? 'Mixed'    : 'Mixto',
    }
    const printModeLabel = synPrintMode === 'join'
      ? (language === 'en' ? 'Match' : 'Unir')
      : (language === 'en' ? 'Write' : 'Escribir')
    const label = `${count} página${count !== 1 ? 's' : ''} · ${gameLabel('synonyms')} · ${gameModeLabels[synGameMode] ?? synGameMode} · ${printModeLabel}`
    setItems(prev => [...prev, {
      itemType: 'random',
      spec: { type: 'synonyms', difficulty: 'medium', gameMode: synGameMode, printMode: synPrintMode, count },
      label,
    }])
    setAddMode(null)
  }

  function removeItem(idx: number) {
    setItems(prev => prev.filter((_, i) => i !== idx))
  }

  function moveUp(idx: number) {
    if (idx === 0) return
    setItems(prev => { const a = [...prev]; [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; return a })
  }

  function moveDown(idx: number) {
    setItems(prev => {
      if (idx >= prev.length - 1) return prev
      const a = [...prev]; [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]]; return a
    })
  }

  return {
    items,
    addMode,
    setAddMode,
    // form state
    wsDiff, setWsDiff,
    wsCount, setWsCount,
    wsThemeIds, setWsThemeIds,
    wsMode, setWsMode,
    sdDiff, setSdDiff,
    sdMode, setSdMode,
    sdCount, setSdCount,
    mtOp, setMtOp,
    mtDiff, setMtDiff,
    mtCount, setMtCount,
    atDiff, setAtDiff,
    atCount, setAtCount,
    atThemeIds, setAtThemeIds,
    atMode, setAtMode,
    symDiff, setSymDiff,
    symMode, setSymMode,
    symCount, setSymCount,
    wmDiff, setWmDiff,
    wmMode, setWmMode,
    wmCount, setWmCount,
    romDiff, setRomDiff,
    romPrintMode, setRomPrintMode,
    romCount, setRomCount,
    hrsDiff, setHrsDiff,
    hrsClockType, setHrsClockType,
    hrsWriteMode, setHrsWriteMode,
    hrsCount, setHrsCount,
    synGameMode, setSynGameMode,
    synPrintMode, setSynPrintMode,
    synCount, setSynCount,
    // actions
    addWordsearch,
    addSudoku,
    addMaths,
    addAttention,
    addSymmetry,
    addWhatsMissing,
    addRoman,
    addHours,
    addSynonyms,
    removeItem,
    moveUp,
    moveDown,
    toggleTheme,
  }
}

// ── Shared style tokens ──────────────────────────────────────────────────────

const nbtnStyle: React.CSSProperties = {
  background:     '#F0EAE0',
  border:         'none',
  borderRadius:   6,
  padding:        '6px 12px',
  cursor:         'pointer',
  fontSize:       '0.875rem',
  minHeight:      32,
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
}

const addFormBox: React.CSSProperties = {
  border:       '1px solid #C8D8C8',
  borderRadius: 8,
  padding:      '12px',
  marginBottom: 10,
  background:   '#F8FDF8',
}

// ── NotebookItemsList ────────────────────────────────────────────────────────

interface NotebookItemsListProps {
  items: NotebookBuilderItem[]
  onRemove: (idx: number) => void
  onMoveUp: (idx: number) => void
  onMoveDown: (idx: number) => void
  showReorder?: boolean
  emptyLabel?: string
}

export function NotebookItemsList({
  items,
  onRemove,
  onMoveUp,
  onMoveDown,
  showReorder = false,
  emptyLabel,
}: NotebookItemsListProps) {
  if (items.length === 0) {
    return (
      <p style={{ fontSize: '0.875rem', color: '#AAA', margin: '4px 0 10px' }}>
        {emptyLabel ?? 'Use the buttons below to add activities.'}
      </p>
    )
  }

  return (
    <div style={{ border: '1px solid #E8DDD0', borderRadius: 8, marginBottom: 10 }}>
      {items.map((item, idx) => (
        <div key={idx} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
          borderBottom: idx < items.length - 1 ? '1px solid #F0EAE0' : undefined,
        }}>
          <span style={{ flex: 1, fontSize: '0.875rem', color: '#1A1A2E' }}>{idx + 1}. {item.label}</span>
          {showReorder && (
            <>
              <button style={nbtnStyle} onClick={() => onMoveUp(idx)} disabled={idx === 0} title="Subir"><FiChevronUp size={14} /></button>
              <button style={nbtnStyle} onClick={() => onMoveDown(idx)} disabled={idx === items.length - 1} title="Bajar"><FiChevronDown size={14} /></button>
            </>
          )}
          <button style={{ ...nbtnStyle, color: '#C62828', padding: '4px 8px' }} onClick={() => onRemove(idx)}><FiX size={14} /></button>
        </div>
      ))}
    </div>
  )
}

// ── NotebookActivityForm ─────────────────────────────────────────────────────

interface NotebookActivityFormProps {
  hook: ReturnType<typeof useNotebookItemsBuilder>
  language: string
  wordsearchThemes: ThemeSummary[]
  attentionThemes: ThemeSummary[]
  labelStyle?: React.CSSProperties
  inputStyle?: React.CSSProperties
  btnPrimaryStyle?: React.CSSProperties
  /** If true, disable the add button when no wordsearch themes are selected */
  requireThemeSelection?: boolean
}

export function NotebookActivityForm({
  hook,
  language,
  wordsearchThemes,
  attentionThemes,
  labelStyle = defaultLabelStyle,
  inputStyle = defaultInputStyle,
  btnPrimaryStyle = defaultBtnPrimaryStyle,
  requireThemeSelection = false,
}: NotebookActivityFormProps) {
  const {
    addMode, setAddMode,
    wsDiff, setWsDiff, wsCount, setWsCount, wsThemeIds, setWsThemeIds, wsMode, setWsMode,
    sdDiff, setSdDiff, sdMode, setSdMode, sdCount, setSdCount,
    mtOp, setMtOp, mtDiff, setMtDiff, mtCount, setMtCount,
    atDiff, setAtDiff, atCount, setAtCount, atThemeIds, setAtThemeIds, atMode, setAtMode,
    symDiff, setSymDiff, symMode, setSymMode, symCount, setSymCount,
    wmDiff, setWmDiff, wmMode, setWmMode, wmCount, setWmCount,
    romDiff, setRomDiff, romPrintMode, setRomPrintMode, romCount, setRomCount,
    hrsDiff, setHrsDiff, hrsClockType, setHrsClockType, hrsWriteMode, setHrsWriteMode, hrsCount, setHrsCount,
    synGameMode, setSynGameMode, synPrintMode, setSynPrintMode, synCount, setSynCount,
    addWordsearch, addSudoku, addMaths, addAttention, addSymmetry, addWhatsMissing,
    addRoman, addHours, addSynonyms, toggleTheme,
  } = hook

  const { t } = useTranslation()
  const dl = t.downloads

  function addBtn(count: string, min: number, max: number, singularEn: string, pluralEn: string, singularEs: string, pluralEs: string) {
    const n = clampCount(count, min, max)
    return language === 'en'
      ? `Add ${n} ${n === 1 ? singularEn : pluralEn}`
      : `Añadir ${n} ${n === 1 ? singularEs : pluralEs}`
  }

  return (
    <>
      {/* Activity type selector */}
      <label style={labelStyle}>{dl.builderAddActivity}</label>
      <select
        style={{ ...inputStyle, marginBottom: 8 }}
        value={addMode ?? ''}
        onChange={e => setAddMode((e.target.value || null) as ActivityType | null)}
      >
        <option value="">— {dl.builderSelectActivity} —</option>
        <option value="wordsearch">{dl.builderAddWordsearch}</option>
        <option value="sudoku">{dl.builderAddSudoku}</option>
        <option value="maths">{dl.builderAddMaths}</option>
        <option value="attention">{dl.builderAddAttention}</option>
        <option value="symmetry">{dl.builderAddSymmetry}</option>
        <option value="whats_missing">{dl.builderAddWhatsMissing}</option>
        <option value="roman">{dl.builderAddRoman}</option>
        <option value="hours">{dl.builderAddHours}</option>
        <option value="synonyms">{dl.builderAddSynonyms}</option>
      </select>

      {/* Wordsearch form */}
      {addMode === 'wordsearch' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={wsDiff} onChange={e => setWsDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderSearchModeLabel}</label>
              <select style={inputStyle} value={wsMode} onChange={e => setWsMode(e.target.value)}>
                <option value="simple">{dl.builderWsModeSimple}</option>
                <option value="diagonals">{dl.builderWsModeDiagonals}</option>
                <option value="reversed">{dl.builderWsModeAllDirs}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderCountLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={wsCount}
                onChange={e => setWsCount(e.target.value)}
                onBlur={() => setWsCount(String(clampCount(wsCount, 1, 50)))}
              />
            </div>
          </div>
          {/* Theme checkboxes */}
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>{dl.builderThemesOptionalLabel}</label>
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #E8DDD0', borderRadius: 6, marginBottom: 10 }}>
              {wordsearchThemes.map(th => {
                const incompatible = wsIncompatible(th, language, wsDiff)
                const count = wsWordCount(th, language, wsDiff)
                return (
                  <label key={th.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                    cursor: incompatible ? 'not-allowed' : 'pointer', borderBottom: '1px solid #F0EAE0',
                    background: wsThemeIds.includes(th.id) ? '#EEF6EE' : 'transparent',
                    opacity: incompatible ? 0.45 : 1,
                  }}>
                    <input
                      type="checkbox"
                      checked={wsThemeIds.includes(th.id)}
                      disabled={incompatible}
                      onChange={() => !incompatible && toggleTheme(th.id, setWsThemeIds)}
                    />
                    <span style={{ fontSize: '0.875rem' }}>{language === 'en' ? th.nameEn : th.nameEs}</span>
                    {incompatible
                      ? <span style={{ fontSize: '0.875rem', color: '#E65100', marginLeft: 'auto' }}>
                          {language === 'en' ? `only ${count} word${count === 1 ? '' : 's'}` : `solo ${count} palabra${count === 1 ? '' : 's'}`}
                        </span>
                      : <span style={{ fontSize: '0.875rem', color: '#888', marginLeft: 'auto' }}>
                          {language === 'en' ? `${th.imageCount} images` : `${th.imageCount} imágenes`}
                        </span>
                    }
                  </label>
                )
              })}
              {wordsearchThemes.length === 0 && (
                <span style={{ fontSize: '0.875rem', color: '#9A8070', padding: '6px 10px' }}>
                  {dl.builderNoThemesMsg}
                </span>
              )}
            </div>
          </div>
          <button
            style={{ ...btnPrimaryStyle, width: '100%' }}
            onClick={addWordsearch}
            disabled={requireThemeSelection && wsThemeIds.length === 0}
          >
            {addBtn(wsCount, 1, 50, 'word search', 'word searches', 'sopa de letras', 'sopas de letras')}
          </button>
        </div>
      )}

      {/* Sudoku form */}
      {addMode === 'sudoku' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={sdDiff} onChange={e => setSdDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderGridLabel}</label>
              <select style={inputStyle} value={sdMode} onChange={e => setSdMode(e.target.value)}>
                <option value="standard">{dl.builderSdModeClassic}</option>
                <option value="mini">{dl.builderSdModeMini}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderCountLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={sdCount}
                onChange={e => setSdCount(e.target.value)}
                onBlur={() => setSdCount(String(clampCount(sdCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addSudoku}>
            {addBtn(sdCount, 1, 50, 'sudoku', 'sudokus', 'sudoku', 'sudokus')}
          </button>
        </div>
      )}

      {/* Maths form */}
      {addMode === 'maths' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>{dl.builderOperationLabel}</label>
              <select style={inputStyle} value={mtOp} onChange={e => setMtOp(e.target.value)}>
                <option value="addition">{dl.builderMtOpAddition}</option>
                <option value="subtraction">{dl.builderMtOpSubtraction}</option>
                <option value="multiplication">{dl.builderMtOpMultiplication}</option>
                <option value="division">{dl.builderMtOpDivision}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={mtDiff} onChange={e => setMtDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderPagesLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={mtCount}
                onChange={e => setMtCount(e.target.value)}
                onBlur={() => setMtCount(String(clampCount(mtCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addMaths}>
            {addBtn(mtCount, 1, 50, 'maths page', 'maths pages', 'página de matemáticas', 'páginas de matemáticas')}
          </button>
        </div>
      )}

      {/* Attention form */}
      {addMode === 'attention' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={atDiff} onChange={e => setAtDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderModeLabel}</label>
              <select style={inputStyle} value={atMode} onChange={e => setAtMode(e.target.value as 'words' | 'numbers')}>
                <option value="words">{dl.builderAtModeWords}</option>
                <option value="numbers">{dl.builderAtModeNumbers}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderCountLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={atCount}
                onChange={e => setAtCount(e.target.value)}
                onBlur={() => setAtCount(String(clampCount(atCount, 1, 50)))}
              />
            </div>
          </div>
          {atMode === 'words' && attentionThemes.length > 0 && (
            <>
              <label style={labelStyle}>{dl.builderThemesOptionalLabel}</label>
              <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #E8DDD0', borderRadius: 6, marginBottom: 10 }}>
                {attentionThemes.map(theme => (
                  <label key={theme.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                    cursor: 'pointer', borderBottom: '1px solid #F0EAE0',
                    background: atThemeIds.includes(theme.id) ? '#EEF6EE' : 'transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={atThemeIds.includes(theme.id)}
                      onChange={() => toggleTheme(theme.id, setAtThemeIds)}
                    />
                    <span style={{ fontSize: '0.875rem' }}>{language === 'en' ? theme.nameEn : theme.nameEs}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addAttention}>
            {addBtn(atCount, 1, 50, 'attention exercise', 'attention exercises', 'ejercicio de atención', 'ejercicios de atención')}
          </button>
        </div>
      )}

      {/* Symmetry form */}
      {addMode === 'symmetry' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={symDiff} onChange={e => setSymDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderSymmetryModeLabel}</label>
              <select style={inputStyle} value={symMode} onChange={e => setSymMode(e.target.value)}>
                <option value="vertical">{dl.builderSymModeVertical}</option>
                <option value="horizontal">{dl.builderSymModeHorizontal}</option>
                <option value="quad">{dl.builderSymModeQuadrant}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderPagesLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={symCount}
                onChange={e => setSymCount(e.target.value)}
                onBlur={() => setSymCount(String(clampCount(symCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addSymmetry}>
            {addBtn(symCount, 1, 50, 'symmetry page', 'symmetry pages', 'página de simetría', 'páginas de simetría')}
          </button>
        </div>
      )}

      {/* What's missing form */}
      {addMode === 'whats_missing' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={wmDiff} onChange={e => setWmDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderModeLabel}</label>
              <select style={inputStyle} value={wmMode} onChange={e => setWmMode(e.target.value as 'emoji' | 'shapes')}>
                <option value="emoji">{dl.builderWmModeEmojis}</option>
                <option value="shapes">{dl.builderWmModeShapes}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderCountLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={wmCount}
                onChange={e => setWmCount(e.target.value)}
                onBlur={() => setWmCount(String(clampCount(wmCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addWhatsMissing}>
            {addBtn(wmCount, 1, 50, '"What\'s missing?" exercise', '"What\'s missing?" exercises', 'ejercicio "¿Qué falta?"', 'ejercicios "¿Qué falta?"')}
          </button>
        </div>
      )}

      {/* Roman Numbers form */}
      {addMode === 'roman' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderDifficultyLabel}</label>
              <select style={inputStyle} value={romDiff} onChange={e => setRomDiff(e.target.value)}>
                <option value="easy">{dl.builderDiffEasy}</option>
                <option value="medium">{dl.builderDiffMedium}</option>
                <option value="hard">{dl.builderDiffHard}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderTypeLabel}</label>
              <select style={inputStyle} value={romPrintMode} onChange={e => setRomPrintMode(e.target.value)}>
                <option value="join">{dl.builderRomPrintJoin}</option>
                <option value="write">{dl.builderRomPrintWrite}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderPagesLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={romCount}
                onChange={e => setRomCount(e.target.value)}
                onBlur={() => setRomCount(String(clampCount(romCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addRoman}>
            {addBtn(romCount, 1, 50, 'Roman Numbers page', 'Roman Numbers pages', 'página de Números Romanos', 'páginas de Números Romanos')}
          </button>
        </div>
      )}

      {/* Hours (Clock Reading) form */}
      {addMode === 'hours' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderModeLabel}</label>
              <select style={inputStyle} value={hrsDiff} onChange={e => setHrsDiff(e.target.value as 'join' | 'write')}>
                <option value="join">{dl.builderHrsModeJoin}</option>
                <option value="write">{dl.builderHrsModeWrite}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderClockTypeLabel}</label>
              <select style={inputStyle} value={hrsClockType} onChange={e => setHrsClockType(e.target.value)}>
                <option value="analog">{dl.builderHrsClockAnalog}</option>
                <option value="digital">{dl.builderHrsClockDigital}</option>
                <option value="mixed">{dl.builderHrsClockMixed}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderPagesLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={hrsCount}
                onChange={e => setHrsCount(e.target.value)}
                onBlur={() => setHrsCount(String(clampCount(hrsCount, 1, 50)))}
              />
            </div>
          </div>
          {hrsDiff === 'write' && (
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>{dl.builderWriteModeLabel}</label>
              <select style={inputStyle} value={hrsWriteMode} onChange={e => setHrsWriteMode(e.target.value)}>
                <option value="write_words">{dl.builderHrsWriteWords}</option>
                <option value="draw_clock">{dl.builderHrsWriteDraw}</option>
              </select>
            </div>
          )}
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addHours}>
            {addBtn(hrsCount, 1, 50, '"What Time Is It?" page', '"What Time Is It?" pages', 'página "¿Qué hora es?"', 'páginas "¿Qué hora es?"')}
          </button>
        </div>
      )}

      {/* Synonyms & Antonyms form */}
      {addMode === 'synonyms' && (
        <div style={addFormBox}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderModeLabel}</label>
              <select style={inputStyle} value={synGameMode} onChange={e => setSynGameMode(e.target.value)}>
                <option value="synonyms">{dl.builderSynModeSynonyms}</option>
                <option value="antonyms">{dl.builderSynModeAntonyms}</option>
                <option value="mixed">{dl.builderSynModeMixed}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderTypeLabel}</label>
              <select style={inputStyle} value={synPrintMode} onChange={e => setSynPrintMode(e.target.value)}>
                <option value="write">{dl.builderSynPrintWrite}</option>
                <option value="join">{dl.builderSynPrintJoin}</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{dl.builderPagesLabel}</label>
              <input
                type="number" min={1} max={50} style={inputStyle}
                value={synCount}
                onChange={e => setSynCount(e.target.value)}
                onBlur={() => setSynCount(String(clampCount(synCount, 1, 50)))}
              />
            </div>
          </div>
          <button style={{ ...btnPrimaryStyle, width: '100%' }} onClick={addSynonyms}>
            {addBtn(synCount, 1, 50, 'Synonyms & Antonyms page', 'Synonyms & Antonyms pages', 'página de Sinónimos y Antónimos', 'páginas de Sinónimos y Antónimos')}
          </button>
        </div>
      )}

    </>
  )
}

// ── Default styles ────────────────────────────────────────────────────────────

const defaultLabelStyle: React.CSSProperties = {
  display:      'block',
  fontSize:     '0.875rem',
  fontWeight:   700,
  color:        '#555',
  marginBottom: 4,
  marginTop:    10,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const defaultInputStyle: React.CSSProperties = {
  width:        '100%',
  boxSizing:    'border-box',
  border:       '1.5px solid #DDD',
  borderRadius: 8,
  padding:      '8px 10px',
  fontSize:     '0.9rem',
  background:   '#fff',
  marginBottom: 4,
}

const defaultBtnPrimaryStyle: React.CSSProperties = {
  background:   '#1B4332',
  color:        '#fff',
  border:       'none',
  borderRadius: 10,
  padding:      '11px 0',
  cursor:       'pointer',
  fontWeight:   700,
  fontSize:     '0.9rem',
  minHeight:    44,
}
